import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core'
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { MatSelectModule } from '@angular/material/select'
import { MatSlideToggleModule } from '@angular/material/slide-toggle'
import { MatSliderModule } from '@angular/material/slider'
import { MatExpansionModule } from '@angular/material/expansion'
import { MatButtonModule } from '@angular/material/button'
import { TranslateModule } from '@ngx-translate/core'
import { GbDashboardSharedUiModule } from 'libs/gb-dashboard/shared/ui/src/lib/gb-dashboard-shared-ui.module'
import {
  GbPresenterItemFilterDialogComponent,
  GbPresenterItemFilterDialogData,
  GbPresenterItemFilterDialogResult,
} from '../gb-presenter-item-filter-dialog/gb-presenter-item-filter-dialog.component'
import { GbDashboardDialogService } from '@galabid/gb-dashboard/shared/utils'
import { GbPresenterToolsTextComponent } from '../gb-presenter-tools-text/gb-presenter-tools-text.component'

@UntilDestroy()
@Component({
  selector: 'gb-presenter-tools-honour-roll',
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatSliderModule,
    MatExpansionModule,
    MatButtonModule,
    TranslateModule,
    GbDashboardSharedUiModule,
    GbPresenterToolsTextComponent,
  ],
  templateUrl: './gb-presenter-tools-honour-roll.component.html',
  styleUrl: './gb-presenter-tools-honour-roll.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GbPresenterToolsHonourRollComponent implements OnInit {
  @Input() toolsFormGroup!: FormGroup

  hideFields = ['font_size', 'font_style']

  borderStyleOptions = [
    { value: 'solid', label: 'PRESENTER.HONOUR_ROLL_TOOLS.BORDER_STYLES.SOLID' },
    { value: 'dashed', label: 'PRESENTER.HONOUR_ROLL_TOOLS.BORDER_STYLES.DASHED' },
    { value: 'dotted', label: 'PRESENTER.HONOUR_ROLL_TOOLS.BORDER_STYLES.DOTTED' },
    { value: 'double', label: 'PRESENTER.HONOUR_ROLL_TOOLS.BORDER_STYLES.DOUBLE' },
  ]

  participantsTransitionOptions = [
    { value: 'scroll-right', label: 'PRESENTER.HONOUR_ROLL_TOOLS.PARTICIPANTS_TRANSITION_OPTIONS.SCROLL_RIGHT' },
    { value: 'scroll-left', label: 'PRESENTER.HONOUR_ROLL_TOOLS.PARTICIPANTS_TRANSITION_OPTIONS.SCROLL_LEFT' },
    { value: 'scroll-top', label: 'PRESENTER.HONOUR_ROLL_TOOLS.PARTICIPANTS_TRANSITION_OPTIONS.SCROLL_UP' },
    { value: 'scroll-bottom', label: 'PRESENTER.HONOUR_ROLL_TOOLS.PARTICIPANTS_TRANSITION_OPTIONS.SCROLL_DOWN' },
  ]

  get latestActivitiesControl(): FormControl {
    return this.toolsFormGroup.get('latest_activities') as FormControl
  }

  get transitionControl(): FormControl {
    return this.toolsFormGroup.get('transition') as FormControl
  }

  get numberActivitiesVisibleControl(): FormControl {
    return this.toolsFormGroup.get('number_activities_visible') as FormControl
  }

  get slideIntervalControl(): FormControl {
    return this.toolsFormGroup.get('slide_interval') as FormControl
  }

  get tableBackgroundColorControl(): FormControl {
    return this.toolsFormGroup.get('table_background_color') as FormControl
  }

  get tablePrimaryColorControl(): FormControl {
    return this.toolsFormGroup.get('table_primary_color') as FormControl
  }

  get tableAccentColorControl(): FormControl {
    return this.toolsFormGroup.get('table_accent_color') as FormControl
  }

  get opacityControl(): FormControl {
    return this.toolsFormGroup.get('opacity') as FormControl
  }

  get fontSizeControl(): FormControl {
    return this.toolsFormGroup.get('font_size') as FormControl
  }

  get showItemAliasControl(): FormControl {
    return this.toolsFormGroup.get('show_item_alias') as FormControl
  }

  get showItemNameControl(): FormControl {
    return this.toolsFormGroup.get('show_item_name') as FormControl
  }

  get borderWidthControl(): FormControl {
    return this.toolsFormGroup.get('border_width') as FormControl
  }

  get borderColorControl(): FormControl {
    return this.toolsFormGroup.get('border_color') as FormControl
  }

  get borderStyleControl(): FormControl {
    return this.toolsFormGroup.get('border_style') as FormControl
  }

  get hiddenItemsControl(): FormControl {
    return this.toolsFormGroup.get('hidden_items') as FormControl
  }

  constructor(private gbDashboardDialogService: GbDashboardDialogService) {}

  ngOnInit(): void {
    this.setupNumberActivitiesVisibleSync()

    // update number of activities initial value
    const rowsValue = this.toolsFormGroup.get('rows')?.value
    const columnsValue = this.toolsFormGroup.get('columns')?.value
    const numberOfActivities = rowsValue * columnsValue
    this.numberActivitiesVisibleControl.setValue(numberOfActivities, { emitEvent: false })
  }

  openFilterItemsDialog(): void {
    const hiddenItemUuids = (this.hiddenItemsControl?.value || []) as string[]

    const dialogRef = this.gbDashboardDialogService.openDialog(GbPresenterItemFilterDialogComponent, {
      width: '750px',
      maxWidth: '95vw',
      data: { hiddenItemUuids },
    })

    dialogRef.afterClosed().subscribe((result: GbPresenterItemFilterDialogResult | undefined) => {
      if (!result) return

      this.hiddenItemsControl.setValue(result.hiddenItemUuids)
      this.hiddenItemsControl.markAsDirty()
      this.toolsFormGroup.markAsDirty()
    })
  }

  private setupNumberActivitiesVisibleSync(): void {
    this.numberActivitiesVisibleControl.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
      if (value) {
        this.updateRowsAndColumns(value)
      }
    })

    this.transitionControl.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      const currentValue = this.numberActivitiesVisibleControl.value
      if (currentValue) {
        this.updateRowsAndColumns(currentValue)
      }
    })
  }

  private updateRowsAndColumns(numberOfActivities: number): void {
    const transition = this.transitionControl.value
    const rowsControl = this.toolsFormGroup.get('rows') as FormControl
    const columnsControl = this.toolsFormGroup.get('columns') as FormControl

    if (!rowsControl || !columnsControl) return

    // For vertical scrolling (up/down), set only 1 column
    // For horizontal scrolling (left/right), set only 1 row
    if (transition === 'scroll-top' || transition === 'scroll-bottom') {
      rowsControl.setValue(numberOfActivities, { emitEvent: false })
      columnsControl.setValue(1, { emitEvent: false })
    } else if (transition === 'scroll-left' || transition === 'scroll-right') {
      rowsControl.setValue(1, { emitEvent: false })
      columnsControl.setValue(numberOfActivities, { emitEvent: false })
    } else {
      rowsControl.setValue(1, { emitEvent: false })
      columnsControl.setValue(numberOfActivities, { emitEvent: false })
    }
  }
}
